import type { NearbyOfficeOption } from "@homeinstead/marketing-api";
import type { CareGiverLeadFormData } from "~www/types/forms/care-giver-lead-form";

import { FormError } from "@homeinstead/ui/components/FormError";
import { Input } from "@homeinstead/ui/components/Input";
import { OfficeSelect } from "@homeinstead/ui/components/OfficeSelect";
import { AnimatePresence, motion } from "motion/react";
import { useFormContext, useWatch } from "react-hook-form";

type OfficeSearchState =
  | "local" // franchise pre-selected, nothing to show
  | "idle" // user hasn’t typed a postal code yet
  | "loading"
  | "error"
  | "no-offices"
  | "has-offices"; // we have nearby orgs to choose from

interface OfficeSearchSectionProps {
  officeOptions: NearbyOfficeOption[];
  officeSearchState: OfficeSearchState;
  officeSelectOpen: boolean;
  setOfficeSelectOpen: (open: boolean) => void;
}

const OfficeSearchSection = ({
  officeOptions,
  officeSearchState,
  officeSelectOpen,
  setOfficeSelectOpen,
}: OfficeSearchSectionProps) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<CareGiverLeadFormData>();

  const postalCode = useWatch({ name: "step2.postalCode" });

  return (
    <>
      <input
        type="hidden"
        value={postalCode}
        {...register("step2.postalCode")}
      />
      <Input
        className="bg-white text-base text-new-beginnings-base"
        id="postal-input"
        maxLength={10}
        placeholder="Postal Code"
        type="text"
        {...register("step2.userEnteredLocation")}
      />

      {officeSearchState === "loading" ? (
        <p className="text-muted-foreground text-sm">Searching for offices…</p>
      ) : null}

      {officeSearchState === "error" ? (
        <FormError.p className="text-error">
          Failed to load offices.
        </FormError.p>
      ) : null}

      {officeSearchState === "no-offices" ? (
        <>
          <FormError.span className="text-error">
            {errors?.step2?.userEnteredLocation?.message}
          </FormError.span>

          <p className="text-error text-sm">No offices found.</p>
        </>
      ) : null}

      {officeSearchState === "has-offices" ? (
        <AnimatePresence mode="wait">
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            initial={{ opacity: 0, y: -5 }}
            key="office-select"
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <OfficeSelect
              officeIdFieldName="step2.organizationNumber"
              officeOptions={officeOptions}
              onOpenChange={setOfficeSelectOpen}
              open={officeSelectOpen}
              postalCodeFieldName="step2.postalCode"
            />
          </motion.div>
        </AnimatePresence>
      ) : null}
    </>
  );
};

export type { OfficeSearchSectionProps, OfficeSearchState };

export { OfficeSearchSection };
